// Notifications redux reducer.
//
// One slice handles bell + settings. Shape:
//   items:        Array<NotificationDoc>     - newest-first
//   unreadCount:  number
//   isLoading:    boolean (list fetch)
//   error:        string|null
//   sseStatus:    'idle'|'connecting'|'open'|'closed'|'unavailable'
//   catalog:      { categories, severities, events, channels } | null
//   preferences:  { events: { <event_code>: {enabled, channels} } } | null
//   prefsLoading: boolean
//
// SSE messages dispatched as RECEIVED prepend to items and bump
// unreadCount so the bell badge updates without polling.

import {
  LIST_REQUEST,    LIST_RESPONSE,    LIST_FAILURE,
  MARK_READ_SUCCESS, MARK_ALL_READ_SUCCESS,
  PREFS_CATALOG_REQUEST,  PREFS_CATALOG_RESPONSE, PREFS_CATALOG_FAILURE,
  PREFS_FETCH_REQUEST,    PREFS_FETCH_RESPONSE,   PREFS_FETCH_FAILURE,
  PREFS_UPDATE_SUCCESS,
  CHANNELS_FETCH_REQUEST, CHANNELS_FETCH_RESPONSE, CHANNELS_FETCH_FAILURE,
  CHANNEL_SAVE_REQUEST,   CHANNEL_SAVE_SUCCESS,    CHANNEL_SAVE_FAILURE,
  CHANNEL_DELETE_SUCCESS,
  CHANNEL_TEST_REQUEST,   CHANNEL_TEST_RESULT,
  RECEIVED, SSE_STATUS,
} from '../../actions/notifications';

const initial = {
  items:        [],
  unreadCount:  0,
  isLoading:    false,
  error:        null,
  // Newest live-pushed notification + a monotonically increasing seq so
  // the header toast can react to every arrival (even a dedupe replay
  // of the same object identity).
  lastReceived: null,
  receivedSeq:  0,
  sseStatus:    'idle',
  catalog:      null,
  preferences:  null,
  prefsLoading: false,
  // Configured delivery endpoints (Slack / Teams / PagerDuty / webhook).
  channels:         [],
  channelsLoading:  false,
  channelSaving:    false,
  channelSaveError: null,
  // { [channel_id]: { testing: bool, ok: bool|null, detail: string } }
  channelTests:     {},
};

export const reducer = (state = initial, action) => {
  switch (action.type) {

    case LIST_REQUEST:
      return { ...state, isLoading: true, error: null };

    case LIST_RESPONSE: {
      const { items = [], unread_count = 0 } = action.payload || {};
      return {
        ...state,
        isLoading:   false,
        items:       items,
        unreadCount: unread_count,
      };
    }

    case LIST_FAILURE:
      return { ...state, isLoading: false, error: action.payload };

    case RECEIVED: {
      // SSE pushed a new notification. Prepend (newest-first), but
      // dedupe if it's already in the list (which can happen when
      // the saga reconnects and the BE replays a "since" delta).
      const incoming = action.payload || {};
      const without = state.items.filter(
        (n) => n.notification_id !== incoming.notification_id,
      );
      const wasUnread = !incoming.read_at;
      return {
        ...state,
        items:        [incoming, ...without],
        unreadCount:  state.unreadCount + (wasUnread ? 1 : 0),
        lastReceived: incoming,
        receivedSeq:  state.receivedSeq + 1,
      };
    }

    case MARK_READ_SUCCESS: {
      const id = action.payload?.notification_id;
      let unreadDelta = 0;
      const items = state.items.map((n) => {
        if (n.notification_id !== id) return n;
        if (!n.read_at) unreadDelta = -1;
        return { ...n, read_at: new Date().toISOString() };
      });
      return {
        ...state,
        items,
        unreadCount: Math.max(0, state.unreadCount + unreadDelta),
      };
    }

    case MARK_ALL_READ_SUCCESS: {
      const now = new Date().toISOString();
      return {
        ...state,
        items: state.items.map(
          (n) => (n.read_at ? n : { ...n, read_at: now }),
        ),
        unreadCount: 0,
      };
    }

    case SSE_STATUS:
      return { ...state, sseStatus: action.payload };

    case PREFS_CATALOG_REQUEST:
      return { ...state, prefsLoading: true };
    case PREFS_CATALOG_RESPONSE:
      return { ...state, prefsLoading: false, catalog: action.payload };
    case PREFS_CATALOG_FAILURE:
      return { ...state, prefsLoading: false };

    case PREFS_FETCH_REQUEST:
      return { ...state, prefsLoading: true };
    case PREFS_FETCH_RESPONSE:
      return { ...state, prefsLoading: false, preferences: action.payload };
    case PREFS_FETCH_FAILURE:
      return { ...state, prefsLoading: false };

    case PREFS_UPDATE_SUCCESS:
      return { ...state, preferences: action.payload };

    case CHANNELS_FETCH_REQUEST:
      return { ...state, channelsLoading: true };
    case CHANNELS_FETCH_RESPONSE:
      return {
        ...state,
        channelsLoading: false,
        channels: action.payload?.items || [],
      };
    case CHANNELS_FETCH_FAILURE:
      return { ...state, channelsLoading: false };

    case CHANNEL_SAVE_REQUEST:
      return { ...state, channelSaving: true, channelSaveError: null };
    case CHANNEL_SAVE_SUCCESS:
      return { ...state, channelSaving: false, channelSaveError: null };
    case CHANNEL_SAVE_FAILURE:
      return { ...state, channelSaving: false, channelSaveError: action.payload };

    case CHANNEL_DELETE_SUCCESS:
      return {
        ...state,
        channels: state.channels.filter(
          (c) => c.channel_id !== action.payload?.channel_id,
        ),
      };

    case CHANNEL_TEST_REQUEST: {
      const id = action.payload?.channel_id;
      return {
        ...state,
        channelTests: {
          ...state.channelTests,
          [id]: { testing: true, ok: null, detail: '' },
        },
      };
    }
    case CHANNEL_TEST_RESULT: {
      const { channel_id, ok, detail } = action.payload || {};
      return {
        ...state,
        channelTests: {
          ...state.channelTests,
          [channel_id]: { testing: false, ok, detail },
        },
      };
    }

    default:
      return state;
  }
};
